import React, { useState, useEffect, useRef } from 'react';
import get from 'lodash/get';
import cn from 'classnames';
import { useDispatch } from 'react-redux';
import useBreakpoint from 'helpers/useBreakPoint';
import history from 'helpers/history';
import {
  NightAssistButton,
  NightAssistButtonMobile,
} from './components/Elements';
import NightAssistModal from './components/NightAssistModal';
import classes from './NightAssist.module.scss';
import { getSecondFromString } from 'containers/TimerButton/utils';
import { getUpdatedUrlQSParam, getFullPath } from 'util/routes';
import { ROUTE_PREV_PATH_PARAM } from 'helpers/constants';
import {
  startNATimer,
  cancelNAtimer,
  getLocationParticipants,
  stopNATimer,
  extendNATimer,
  updateNATimer,
} from 'store/actions/nightAssist';
import { useIndexData } from './selectorData';
import { setUserSettings } from 'store/actions/userSettings';
import { getToday } from 'util/date';

let naTimerInterval = null;
let naTimerStartTimestamp = null;
const WARNING_COUNTDOWN_VALUE =
  parseInt(import.meta.env.VITE_NA_WARNING_TIMEOUT || 5, 10) * 60;
const NightAssist = ({ className }) => {
  const pathname = history?.location?.pathname;
  const isCreateNAReport = pathname.startsWith('/create-na-incident-reports');
  const isTablet = useBreakpoint('md', 'down');
  const dispatch = useDispatch();
  const {
    isStarting,
    normalTimerRunning,
    timerRunning,
    locationParticipants,
    stopTimerLoading,
    extendTimerLoading,
    updateTimerLoading,
    submittingNA,
    userTimeZone,
  } = useIndexData();
  const isNASubmitting = !!submittingNA?.isSubmitting;
  const naBlockMobileRef = useRef(null);
  const normalTimerLocationId = get(normalTimerRunning, 'location.id', '');
  const thisUrl = getUpdatedUrlQSParam(getFullPath(history));
  const [addDes, setAddDes] = useState(false);
  const [isOver25m, setIsOver25m] = useState(false);
  const [isOver30m, setIsOver30m] = useState(false);
  const [locationMeta, setLocationMeta] = useState({});
  const [isOpen, setIsOpen] = useState(false);
  const [timeCount, setTimeCount] = useState(0);
  const warnedForHMS = timerRunning.warned_for_hms;
  const handleTimeCountChange = val => {
    setTimeCount(val);
  };
  const resetTimeCountToZero = () => {
    naTimerStartTimestamp = Date.now();
    handleTimeCountChange(0);
  };
  const resetTimer = keepModal => {
    resetTimeCountToZero();
    naTimerStartTimestamp = null;
    clearInterval(naTimerInterval);
    naTimerInterval = null;
    if (!keepModal) {
      setIsOpen(false);
    }
  };
  const handleGetLocations = (search, page = 1, isMore, cb) => {
    dispatch(
      getLocationParticipants(
        normalTimerLocationId,
        {
          q: search || '',
          page: page,
          isMore,
        },
        resp => {
          setLocationMeta(resp.meta);
          if (cb) cb(resp);
        }
      )
    );
  };
  const handleSubmit = (values, { addDes, isOver25m }, cb) => {
    if (addDes) {
      dispatch(
        stopNATimer(
          {
            description: values.description,
            participant_id: values.participant,
            require_incident_report: values.require_incident_report,
          },
          () => {
            resetTimer();
          }
        )
      );
    } else if (isOver25m) {
      dispatch(
        extendNATimer({}, () => {
          resetTimeCountToZero();
          if (cb) cb();
        })
      );
    } else {
      dispatch(
        updateNATimer(
          {
            description: values.description,
            participant_id: values.participant,
            require_incident_report: values.require_incident_report,
          },
          () => {
            resetTimer();
          }
        )
      );
    }
  };
  const handleOtherStateModal = ({ warnedCountDownTime, countDownTime }) => {
    if (
      isOver25m &&
      !isOver30m &&
      warnedCountDownTime >= WARNING_COUNTDOWN_VALUE
    ) {
      setIsOver30m(true);
      setAddDes(false);
      resetTimer(true);
    } else if (!!warnedForHMS && !isOver25m && !timerRunning.extended_at) {
      resetTimeCountToZero();
      setIsOver30m(false);
      setIsOver25m(true);
      setAddDes(false);
    } else if (
      isOver25m &&
      !!timerRunning.extended_at &&
      !timerRunning.warned_at
    ) {
      resetTimeCountToZero();
      setIsOver25m(false);
    }
  };
  const handleStartReport = () => {
    dispatch(
      setUserSettings(
        'submittingNA',
        '',
        {
          isSubmitting: true,
          startTime: getToday(userTimeZone)
            .subtract(
              getSecondFromString(timerRunning.hms) + timeCount,
              'seconds'
            )
            .format('h:mma'),
          stopTime: getToday(userTimeZone).format('h:mma'),
        },
        true,
        true
      )
    );
    history.push(
      getUpdatedUrlQSParam(
        '/create-na-incident-reports',
        isCreateNAReport
          ? {}
          : {
              [ROUTE_PREV_PATH_PARAM]: thisUrl,
            }
      )
    );
  };
  const modalProps = {
    timeCount,
    isOpen: isOpen && !isNASubmitting,
    onClose: () => {
      dispatch(cancelNAtimer());
      setIsOpen(false);
    },
    handleGetLocations,
    locationMeta,
    onSubmit: handleSubmit,
    timerRunning,
    locationParticipants,
    loading: stopTimerLoading || extendTimerLoading || updateTimerLoading,
    isOver25m,
    setIsOver25m,
    isOver30m,
    addDes,
    setAddDes,
    handleOtherStateModal,
    onStartReport: handleStartReport,
  };
  const handleStartTimer = () => {
    dispatch(startNATimer());
    setIsOver25m(false);
    setIsOver30m(false);
    setAddDes(false);
  };
  useEffect(() => {
    if (isStarting) {
      setIsOver25m(false);
      setIsOver30m(false);
      setAddDes(false);
      if (!naTimerInterval) {
        naTimerStartTimestamp = Date.now();
        naTimerInterval = setInterval(() => {
          const elapsedSeconds = Math.floor(
            (Date.now() - naTimerStartTimestamp) / 1000
          );
          setTimeCount(elapsedSeconds);
        }, 1000);
      }
      handleOtherStateModal({
        countDownTime: getSecondFromString(timerRunning?.hms),
        warnedCountDownTime: timerRunning?.warned_for || 0,
      });
      setIsOpen(true);
    } else {
      resetTimer();
    }
    return () => {
      resetTimer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStarting]);

  return (
    <div className={cn(classes.wrapper, className)}>
      {(!isNASubmitting || !isStarting) && (
        <>
          {isTablet ? (
            <NightAssistButtonMobile className={classes.naButtonMobile} />
          ) : (
            <NightAssistButton onClick={handleStartTimer} />
          )}
          <div
            className={cn(classes.naBlockMobile, {
              [classes.hide]: !isTablet,
              [classes.atDashboard]: pathname === '/dashboard',
            })}
            ref={naBlockMobileRef}
          >
            {isTablet && (
              <NightAssistButton
                onClick={() => {
                  handleStartTimer();
                }}
                hideIcon
                text="Log Night Assist"
                size="large"
              />
            )}
          </div>
        </>
      )}
      <NightAssistModal {...modalProps} />
    </div>
  );
};

export default NightAssist;
